/**
 * StxProfileEditor · P6 (Bereichstrennung §10.2 / §11)
 *
 * Eigenständiger Editor für die öffentliche stilltouchedX-Identität. Optisch als
 * eigener, umrahmter Bereich — im GLEICHEN hellen Stil wie der Rest der Profil-Seite
 * (kein Dark-Block). Strikt getrennt vom Social-Profil, speichert über /api/stx/profile.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Loader2, Camera, Lock, AlertTriangle, Crop, ChevronRight } from "lucide-react";
import ImageCropDialog from "@/components/home/ImageCropDialog";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import AIAssistBar from "@/components/ai/AIAssistBar";

export default function StxProfileEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, eligible: false, exists: false });
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState("");
  const [cover, setCover] = useState("");
  const [cardUrl, setCardUrl] = useState("");
  const [optin, setOptin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [cropSource, setCropSource] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 'avatar' | 'cover'
  const [cardCrop, setCardCrop] = useState(false);
  const [anonJob, setAnonJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showWarn, setShowWarn] = useState(false);
  const avatarRef = useRef(null);
  const coverRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/stx/profile");
      const p = data.profile || {};
      setState({ loading: false, eligible: !!data.eligible, exists: !!data.exists });
      setDisplayName(p.stx_display_name || "");
      setHandle(p.stx_handle || "");
      setBio(p.stx_bio || "");
      setAvatar(p.stx_avatar || "");
      setCover(p.stx_cover || "");
      setCardUrl((p.stx_creator_card && p.stx_creator_card.url) || "");
      setOptin(!!p.public_link_optin);
    } catch {
      setState({ loading: false, eligible: false, exists: false });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError(""); setSuccess(false); setSaving(true);
    try {
      await api.put("/stx/profile", {
        stx_display_name: displayName,
        stx_handle: handle || undefined,
        stx_bio: bio || undefined,
        stx_avatar: avatar || undefined,
        stx_cover: cover || undefined,
        stx_creator_card: cardUrl ? { url: cardUrl } : undefined,
      });
      setSuccess(true);
      setState((s) => ({ ...s, exists: true }));
      setTimeout(() => setSuccess(false), 3500);
    } catch (err) {
      setError(formatError(err) || t("stx_profile.save_failed", { defaultValue: "Speichern fehlgeschlagen." }));
    } finally { setSaving(false); }
  };

  const applyLink = async (enabled) => {
    setError("");
    try {
      await api.patch("/stx/profile/link", { enabled, confirm: enabled });
      setOptin(enabled);
      setShowWarn(false);
    } catch (err) {
      setError(formatError(err) || t("stx_profile.link_failed", { defaultValue: "Verknüpfung fehlgeschlagen." }));
    }
  };

  const uploadBlob = async (blob, name) => {
    const fd = new FormData();
    fd.append("file", new File([blob], name, { type: blob.type || "image/jpeg" }));
    const { data } = await api.post("/upload?area_scope=STILLTOUCHEDX", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `/api/files/${data.path}?v=${Date.now()}`;
  };

  if (state.loading) return null;

  const lbl = "text-xs font-medium text-[#666] uppercase tracking-wider";
  const fieldCls = "mt-1 w-full h-10 px-4 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]";
  const imgBtn = "mt-2 h-9 px-4 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40";

  return (
    <section data-testid="stx-profile-editor" className="px-4 pb-8">
      <div className="rounded-xl border border-[#E5E5E5] bg-white p-4">
        <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-1">stilltouchedX-Profil</p>
        <p className="text-xs text-[#666]">{t("stx_profile.subtitle", { defaultValue: "Deine separate, anonyme Ü18-Identität. Nicht mit dem Social-Profil verbunden – außer du aktivierst die Verknüpfung unten." })}</p>

        {!state.eligible ? (
          <button
            type="button"
            data-testid="stx-profile-locked"
            onClick={() => navigate("/profile/access")}
            className="mt-4 w-full rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3 flex items-center gap-2 text-left hover:bg-[#F0F0F0] transition-colors"
          >
            <Lock size={16} className="text-[#999] shrink-0" />
            <span className="flex-1 text-xs text-[#666]">
              {t("stx_profile.locked", { defaultValue: "Das stilltouchedX-Profil ist nur für freigeschaltete stilltouchedX-Seller verfügbar." })}
              <span className="block mt-0.5 text-[#0A0A0A] font-medium">Jetzt freischalten → „Meine Bereiche verwalten"</span>
            </span>
            <ChevronRight size={16} className="text-[#999] shrink-0" />
          </button>
        ) : (
          <div className="mt-4 space-y-5">
            {error && <p data-testid="stx-profile-error" className="text-xs text-red-500">{error}</p>}
            {success && <p data-testid="stx-profile-success" className="text-xs text-green-600">{t("stx_profile.saved", { defaultValue: "Gespeichert." })}</p>}

            {/* Avatar — ein Button (Mobil: Kamera/Galerie via OS-Auswahl) */}
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-[#E5E5E5] shrink-0 flex items-center justify-center">
                {avatar ? <img data-testid="stx-avatar-preview" src={avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-[#999] text-xs">Avatar</span>}
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#666]">{t("stx_profile.avatar_label", { defaultValue: "stilltouchedX-Avatar" })}</p>
                <button type="button" data-testid="stx-avatar-btn" onClick={() => avatarRef.current?.click()} disabled={uploading} className={imgBtn}>
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {t("stx_profile.upload", { defaultValue: "Bild wählen" })}
                </button>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setCropSource(f); setCropTarget("avatar"); } e.target.value = ""; }} />
              </div>
            </div>

            {/* Cover — ein Button */}
            <div className="flex items-start gap-4">
              <div className="w-28 rounded-lg overflow-hidden bg-[#E5E5E5] shrink-0 flex items-center justify-center" style={{ aspectRatio: "16 / 9" }}>
                {cover ? <img data-testid="stx-cover-preview" src={cover} alt="" className="w-full h-full object-cover" /> : <span className="text-[#999] text-[10px]">16:9</span>}
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#666]">{t("stx_profile.cover_label", { defaultValue: "stilltouchedX-Cover" })}</p>
                <button type="button" data-testid="stx-cover-btn" onClick={() => coverRef.current?.click()} disabled={uploading} className={imgBtn}>
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {t("stx_profile.upload", { defaultValue: "Bild wählen" })}
                </button>
                <input ref={coverRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setCropSource(f); setCropTarget("cover"); } e.target.value = ""; }} />
              </div>
            </div>

            {/* Creator-Card — 3:1-Zuschnitt AUS dem bereits hochgeladenen Cover (kein separater Upload) */}
            <div className="p-3 border border-[#E5E5E5] rounded-lg">
              <p className="text-xs font-medium text-[#0A0A0A]">{t("stx_profile.card_label", { defaultValue: "stilltouchedX-Creator-Card" })}</p>
              <p className="text-[10px] text-[#999] mt-0.5">{t("stx_profile.card_desc", { defaultValue: "Breiter 3:1-Ausschnitt aus deinem stilltouchedX-Cover." })}</p>
              {cover ? (
                <div className="flex items-start gap-4 mt-3">
                  <div className="w-28 rounded-lg overflow-hidden bg-[#E5E5E5] shrink-0" style={{ aspectRatio: "3 / 1" }}>
                    <img data-testid="stx-card-preview" src={cardUrl || cover} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button type="button" data-testid="stx-card-btn" onClick={() => setCardCrop(true)} disabled={uploading}
                    className="h-9 px-4 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40">
                    <Crop size={12} /> {t("stx_profile.card_crop", { defaultValue: "Ausschnitt wählen" })}
                  </button>
                </div>
              ) : (
                <div data-testid="stx-card-disabled" className="mt-3 rounded-lg border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-2 text-[11px] text-[#999]">
                  {t("stx_profile.card_needs_cover", { defaultValue: "Lade zuerst ein stilltouchedX-Cover hoch, um die Creator-Card zuzuschneiden." })}
                </div>
              )}
            </div>

            {/* Username */}
            <div>
              <label className={lbl}>{t("stx_profile.username", { defaultValue: "stilltouchedX-Username" })}</label>
              <input data-testid="stx-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value.slice(0, 50))} className={fieldCls} minLength={2} maxLength={50} />
            </div>

            {/* Bio (explizite Textgenerierung erlaubt, area="STX") */}
            <div>
              <label className={lbl}>{t("stx_profile.bio", { defaultValue: "stilltouchedX-Bio" })}</label>
              <textarea data-testid="stx-bio" value={bio} onChange={(e) => setBio(e.target.value.slice(0, 1000))} rows={4}
                className="mt-1 w-full px-4 py-2 border border-[#E5E5E5] bg-white text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
              <AIAssistBar area="STX" value={bio} onApply={setBio} fieldType="bio" testIdSuffix="stx-bio" />
            </div>

            {/* Opt-in Verknüpfung */}
            <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="stx-link-optin" checked={optin}
                  onChange={(e) => { if (e.target.checked) { setShowWarn(true); } else { applyLink(false); } }}
                  className="mt-0.5 accent-[#0A0A0A]" />
                <span className="text-xs text-[#0A0A0A]">
                  {t("stx_profile.optin_label", { defaultValue: "Mein Social-Profil und mein stilltouchedX-Profil öffentlich miteinander verknüpfen" })}
                </span>
              </label>
            </div>

            <button type="button" data-testid="stx-profile-save" onClick={save} disabled={saving}
              className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin inline" /> : t("stx_profile.save", { defaultValue: "stilltouchedX-Profil speichern" })}
            </button>
          </div>
        )}
      </div>

      {/* Crop-Dialog für Avatar/Cover (neue Datei) */}
      {cropSource && cropTarget && (
        <ImageCropDialog
          file={cropSource}
          aspect={cropTarget === "avatar" ? 1 : 16 / 9}
          title={t("stx_profile.crop_title", { defaultValue: "Bildausschnitt wählen" })}
          onCancel={() => { setCropSource(null); setCropTarget(null); }}
          onCropped={(cropped) => {
            const target = cropTarget;
            setCropSource(null); setCropTarget(null);
            const named = new File([cropped], `stx-${target}.jpg`, { type: cropped.type || "image/jpeg" });
            setAnonJob({ file: named, target });
          }}
        />
      )}

      {/* Creator-Card 3:1-Zuschnitt aus dem bestehenden Cover */}
      {cardCrop && cover && (
        <ImageCropDialog
          src={cover}
          aspect={3}
          title={t("stx_profile.card_crop_title", { defaultValue: "Creator-Card zuschneiden (3:1)" })}
          onCancel={() => setCardCrop(false)}
          onCropped={async (blob) => {
            setCardCrop(false); setUploading(true); setError("");
            try { setCardUrl(await uploadBlob(blob, "stx-card.jpg")); }
            catch (err) { setError(formatError(err)); }
            finally { setUploading(false); }
          }}
        />
      )}

      {/* Anonymisierungs-Engine vor Avatar/Cover-Upload */}
      {anonJob && (
        <AnonymizeDialog
          file={anonJob.file}
          context={anonJob.target === "cover" ? "PROFILE_COVER" : "PROFILE_AVATAR"}
          onCancel={() => setAnonJob(null)}
          onComplete={(finalUrl) => {
            const target = anonJob.target;
            setAnonJob(null);
            const url = `${finalUrl}?v=${Date.now()}`;
            if (target === "cover") setCover(url); else setAvatar(url);
          }}
        />
      )}

      {/* Warnhinweis beim Aktivieren der Verknüpfung (§11.3) */}
      {showWarn && (
        <div data-testid="stx-link-warning" className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowWarn(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-600 mb-2">
              <AlertTriangle size={20} />
              <p className="text-sm font-bold text-[#0A0A0A]">{t("stx_profile.warn_title", { defaultValue: "Profile öffentlich verknüpfen?" })}</p>
            </div>
            <p className="text-xs text-[#666] leading-relaxed">
              {t("stx_profile.warn_body", { defaultValue: "Durch die Aktivierung werden dein Social-Profil und dein stilltouchedX-Profil öffentlich miteinander verknüpft. Andere Nutzer können dadurch erkennen, dass beide Profile zu dir gehören. Achte insbesondere bei Social-Beiträgen auf Bilder, Texte, Orte oder andere Angaben, die Rückschlüsse auf deine Identität zulassen." })}
            </p>
            <div className="flex gap-2 mt-4">
              <button data-testid="stx-link-cancel" onClick={() => setShowWarn(false)} className="flex-1 h-10 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#FAFAFA]">
                {t("stx_profile.warn_cancel", { defaultValue: "Abbrechen" })}
              </button>
              <button data-testid="stx-link-confirm" onClick={() => applyLink(true)} className="flex-1 h-10 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600">
                {t("stx_profile.warn_confirm", { defaultValue: "Verknüpfen" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
